var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Type } from '@sinclair/typebox';
import { postWithRetries } from "../../http.mjs";
import { logger } from "../../log.mjs";
import { elementInstance } from "../instance.mjs";
import { paymentElementId, paymentIntentClientSecret } from "./elements.mjs";
import { getBillingAddress, getShippingAddress, loadStripeIfNeeded } from "./utils.mjs";
import { getShippingAddressElement } from "./shippingAddressElements.mjs";
import { getBillingAddressElement } from "./billingAddressElements.mjs";
export const initializeStripePaymentElements = (state, actions, cartPayment) => {
    elementInstance({
        elementId: paymentElementId,
        idempotencyKey: state.id + state.version + paymentIntentClientSecret(state),
        onMount: (e) => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            const stripe = yield loadStripeIfNeeded(cartPayment);
            const elementsOptions = (_b = (_a = state.paymentProviderElementsOptions) === null || _a === void 0 ? void 0 : _a.stripe) === null || _b === void 0 ? void 0 : _b.payment.elements;
            const elements = stripe.elements({
                clientSecret: paymentIntentClientSecret(state),
                locale: elementsOptions === null || elementsOptions === void 0 ? void 0 : elementsOptions.locale,
                appearance: elementsOptions === null || elementsOptions === void 0 ? void 0 : elementsOptions.appearance,
                fonts: elementsOptions === null || elementsOptions === void 0 ? void 0 : elementsOptions.fonts,
                loader: elementsOptions === null || elementsOptions === void 0 ? void 0 : elementsOptions.loader,
            });
            const paymentElement = createPaymentElement(elements, state);
            paymentElement.mount(e);
            actions.setCompletePurchaseHandler(() => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                const billingAddressElement = getBillingAddressElement();
                const shippingAddressElement = getShippingAddressElement();
                if (!shippingAddressElement) {
                    state.onUnexpectedError('Shipping address element not mounted');
                    return;
                }
                const shippingDetails = yield shippingAddressElement.getValue();
                const billingDetails = yield (billingAddressElement === null || billingAddressElement === void 0 ? void 0 : billingAddressElement.getValue());
                if (!shippingDetails.complete) {
                    return {
                        $type: 'validationError',
                        errors: [{ message: `Complete shipping address not provided` }],
                    };
                }
                let billingDetailsFinal = shippingDetails;
                if (state.customer.useBillingAddress) {
                    if (!(billingDetails === null || billingDetails === void 0 ? void 0 : billingDetails.complete)) {
                        return {
                            $type: 'validationError',
                            errors: [{ message: `Complete billing address not provided` }],
                        };
                    }
                    billingDetailsFinal = billingDetails;
                }
                yield savePaymentIntentCustomerData(state, shippingDetails, billingDetails);
                const { error: submitError } = yield elements.submit();
                if (submitError) {
                    return {
                        $type: 'validationError',
                        errors: [{ message: (_a = submitError.message) !== null && _a !== void 0 ? _a : 'An unexpected error occurred' }],
                    };
                }
                const confirmParams = getConfirmParams(state, billingDetailsFinal);
                const { error } = yield stripe.confirmPayment({ elements, confirmParams });
                if (error) {
                    return {
                        $type: 'validationError',
                        errors: [{ message: error.message || 'Payment failed' }],
                    };
                }
                // No need to do anything here because in this case the payment was successful and the
                // user will be redirected
            }));
            return () => paymentElement.unmount();
        }),
    });
};
const createPaymentElement = (elements, state) => {
    var _a;
    const elementOptions = (_a = state.paymentProviderElementsOptions.stripe) === null || _a === void 0 ? void 0 : _a.payment.element;
    return elements.create('payment', {
        fields: {
            billingDetails: {
                address: 'if_required',
            },
        },
        layout: elementOptions === null || elementOptions === void 0 ? void 0 : elementOptions.layout,
    });
};
const getConfirmParams = (state, billingDetails) => {
    return {
        payment_method_data: {
            billing_details: {
                name: billingDetails.value.name,
                email: state.customer.email,
                phone: state.customer.phoneNumber,
                address: {
                    line1: billingDetails.value.address.line1,
                    line2: billingDetails.value.address.line2,
                    city: billingDetails.value.address.city,
                    state: billingDetails.value.address.state,
                    postal_code: billingDetails.value.address.postal_code,
                    country: billingDetails.value.address.country,
                },
            },
        },
        // Stripe will include query params to this url that we pick up on and check for a
        // status update on the payment intent
        return_url: state.checkoutPageUrl,
    };
};
const savePaymentIntentCustomerData = (state, shippingDetails, billingDetails) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const body = {
        address: getShippingAddress(shippingDetails),
        billingAddress: billingDetails ? getBillingAddress(billingDetails) : null,
        email: (_a = state.customer.email) !== null && _a !== void 0 ? _a : null,
        phoneNumber: (_b = state.customer.phoneNumber) !== null && _b !== void 0 ? _b : null,
    };
    try {
        yield postWithRetries(`/carts/${state.id}/${state.version}/payment/customer`, state.checkoutApiBaseUrl, body, Type.Unknown(), state.token);
    }
    catch (error) {
        logger.error({ error }, 'An unexpected error occurred');
        state.onUnexpectedError('An unexpected error occurred');
    }
});
//# sourceMappingURL=paymentElements.js.map